import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, CheckCircle, XCircle, Clock, Download, Eye, X } from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { DocumentStatusBadge } from '../components/Documents/DocumentStatusBadge';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import { ProfileType } from '../types';
import type { Document } from '../types/documents';
import { DocumentStatus } from '../types/documents';

const getDocumentStatus = (doc: any): DocumentStatus => {
  if (doc.is_validated === true) {
    return DocumentStatus.VERIFIED;
  } else if (doc.is_validated === false && doc.validated_by !== null) {
    return DocumentStatus.REJECTED;
  } else {
    return DocumentStatus.UPLOADED;
  }
};

export const AllDocumentsPreviewPage: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectOwner, setProjectOwner] = useState<any>(null);
  const [advisorId, setAdvisorId] = useState<string | null>(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [documentToReject, setDocumentToReject] = useState<Document | null>(null);

  useEffect(() => {
    if (projectId) {
      loadProjectAndDocuments();
    }
  }, [projectId]);

  const loadProjectAndDocuments = async () => {
    try {
      setIsLoading(true);

      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select(`
          *,
          owner:users!projects_owner_id_fkey(id, full_name, email)
        `)
        .eq('id', projectId)
        .single();

      if (projectError) throw projectError;

      setProjectName(project?.name || '');
      setProjectOwner(project?.owner);
      setAdvisorId(project?.assigned_advisor_id);

      const { data: docs, error: docsError } = await supabase
        .from('documents')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (docsError) throw docsError;

      setDocuments(docs || []);
      if (docs && docs.length > 0) {
        setSelectedDocument(docs[0]);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (document: Document, newStatus: 'UPLOADED' | 'VERIFIED' | 'REJECTED', note?: string) => {
    if (!projectId) return;

    setIsUpdating(true);
    try {
      const isValidated = newStatus === 'VERIFIED' ? true : false;
      const validatedBy = newStatus !== 'UPLOADED' ? user?.id : null;
      const validatedAt = newStatus !== 'UPLOADED' ? new Date().toISOString() : null;

      const { error: updateError } = await supabase
        .from('documents')
        .update({
          is_validated: isValidated,
          validated_by: validatedBy,
          validated_at: validatedAt,
          validation_note: note || null
        })
        .eq('id', document.id);

      if (updateError) throw updateError;

      const statusText = newStatus === 'VERIFIED' ? 'validé' : newStatus === 'REJECTED' ? 'rejeté' : 'remis en attente';

      const notificationTitle = newStatus === 'VERIFIED'
        ? 'Document validé'
        : newStatus === 'REJECTED'
          ? 'Document rejeté'
          : 'Document en attente';

      const notificationMessage = `Le document "${document.filename}" a été ${statusText}${note ? ` : ${note}` : ''}`;

      const notificationsToCreate = [];

      if (projectOwner?.id) {
        notificationsToCreate.push({
          user_id: projectOwner.id,
          type: newStatus === 'VERIFIED' ? 'DOCUMENT_VALIDATED' : 'DOCUMENT_REJECTED',
          title: notificationTitle,
          message: notificationMessage,
          link: `/projects/${projectId}/documents`,
          metadata: {
            project_id: projectId,
            document_id: document.id,
            status: newStatus
          }
        });
      }

      if (advisorId && advisorId !== user?.id) {
        notificationsToCreate.push({
          user_id: advisorId,
          type: newStatus === 'VERIFIED' ? 'DOCUMENT_VALIDATED' : 'DOCUMENT_REJECTED',
          title: notificationTitle,
          message: notificationMessage,
          link: `/projects/${projectId}/documents`,
          metadata: {
            project_id: projectId,
            document_id: document.id,
            status: newStatus
          }
        });
      }

      if (notificationsToCreate.length > 0) {
        const { error: notifError } = await supabase
          .from('notifications_enhanced')
          .insert(notificationsToCreate);

        if (notifError) {
          console.error('Error creating notifications:', notifError);
        }
      }

      await loadProjectAndDocuments();
    } catch (error) {
      console.error('Error updating document status:', error);
      alert('Erreur lors de la mise à jour du statut');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReject = (document: Document) => {
    setDocumentToReject(document);
    setRejectionNote('');
    setShowRejectionModal(true);
  };

  const confirmReject = async () => {
    if (documentToReject) {
      await handleStatusChange(documentToReject, 'REJECTED', rejectionNote);
      setShowRejectionModal(false);
      setDocumentToReject(null);
      setRejectionNote('');
    }
  };

  const canManage = user && [ProfileType.ACCOMPAGNATEUR, ProfileType.COORDINATEUR, ProfileType.ADMIN].includes(user.profile_type as ProfileType);

  const getStatusCounts = () => {
    return {
      total: documents.length,
      validated: documents.filter(d => d.is_validated === true).length,
      pending: documents.filter(d => d.is_validated === false && d.validated_by === null).length,
      rejected: documents.filter(d => d.is_validated === false && d.validated_by !== null).length
    };
  };

  const stats = getStatusCounts();

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-screen flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Documents du projet</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{projectName}</p>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-center px-4 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.validated}</div>
                <div className="text-xs text-green-700 dark:text-green-300">Validés</div>
              </div>
              <div className="text-center px-4 py-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.pending}</div>
                <div className="text-xs text-orange-700 dark:text-orange-300">En attente</div>
              </div>
              <div className="text-center px-4 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.rejected}</div>
                <div className="text-xs text-red-700 dark:text-red-300">Rejetés</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          <div className="w-1/3 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
            <div className="p-4 space-y-2">
              {documents.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Aucun document trouvé</p>
                </div>
              ) : (
                documents.map((doc) => (
                  <div
                    key={doc.id}
                    onClick={() => setSelectedDocument(doc)}
                    className={`p-4 rounded-lg cursor-pointer transition-all ${
                      selectedDocument?.id === doc.id
                        ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-500'
                        : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                          {doc.filename}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {doc.document_type || 'Document'}
                        </p>
                      </div>
                      <DocumentStatusBadge status={getDocumentStatus(doc)} />
                    </div>
                    {doc.created_at && (
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col bg-white dark:bg-gray-800">
            {selectedDocument ? (
              <>
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        {selectedDocument.filename}
                      </h2>
                      <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                        <span>{selectedDocument.document_type || 'Document'}</span>
                        <span>•</span>
                        <span>
                          {selectedDocument.file_size
                            ? (selectedDocument.file_size / (1024 * 1024)).toFixed(2) + ' MB'
                            : 'Taille inconnue'}
                        </span>
                        {selectedDocument.created_at && (
                          <>
                            <span>•</span>
                            <span>
                              {new Date(selectedDocument.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </>
                        )}
                      </div>
                      {selectedDocument.validation_note && (
                        <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                          <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            <strong>Note :</strong> {selectedDocument.validation_note}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <DocumentStatusBadge status={getDocumentStatus(selectedDocument)} />
                      {selectedDocument.file_url && (
                        <a
                          href={selectedDocument.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg transition-colors"
                          title="Télécharger"
                        >
                          <Download className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  </div>

                  {canManage && (
                    <div className="mt-4 flex items-center space-x-2">
                      <button
                        onClick={() => handleStatusChange(selectedDocument, 'UPLOADED')}
                        disabled={isUpdating || getDocumentStatus(selectedDocument) === DocumentStatus.UPLOADED}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Clock className="w-4 h-4" />
                        <span>En attente</span>
                      </button>
                      <button
                        onClick={() => handleStatusChange(selectedDocument, 'VERIFIED')}
                        disabled={isUpdating || getDocumentStatus(selectedDocument) === DocumentStatus.VERIFIED}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Valider</span>
                      </button>
                      <button
                        onClick={() => handleReject(selectedDocument)}
                        disabled={isUpdating || getDocumentStatus(selectedDocument) === DocumentStatus.REJECTED}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <XCircle className="w-4 h-4" />
                        <span>Rejeter</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-hidden bg-gray-100 dark:bg-gray-900">
                  {selectedDocument.file_url ? (
                    selectedDocument.mime_type === 'application/pdf' ? (
                      <object
                        data={selectedDocument.file_url}
                        type="application/pdf"
                        className="w-full h-full"
                      >
                        <iframe
                          src={`https://docs.google.com/viewer?url=${encodeURIComponent(selectedDocument.file_url)}&embedded=true`}
                          className="w-full h-full border-0"
                          title={selectedDocument.filename}
                        />
                      </object>
                    ) : selectedDocument.mime_type?.startsWith('image/') ? (
                      <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                        <img
                          src={selectedDocument.file_url}
                          alt={selectedDocument.filename}
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            console.error('Error loading image:', selectedDocument.file_url);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500 dark:text-gray-400 mb-4">
                            Aperçu non disponible pour ce type de fichier
                          </p>
                          <a
                            href={selectedDocument.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            <span>Télécharger</span>
                          </a>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">
                          Aperçu non disponible
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Eye className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    Sélectionnez un document pour le prévisualiser
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {showRejectionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Rejeter le document
                </h3>
                <button
                  onClick={() => setShowRejectionModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Motif du rejet (optionnel)
                </label>
                <textarea
                  value={rejectionNote}
                  onChange={(e) => setRejectionNote(e.target.value)}
                  placeholder="Expliquez pourquoi ce document est rejeté..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowRejectionModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmReject}
                  disabled={isUpdating}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdating ? 'Rejet...' : 'Confirmer le rejet'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
